/**
 * Голосовая заметка к уже заведённому камню.
 *
 * Та же механика, что на съёмке: аудио остаётся источником истины, текст —
 * его расшифровка, а из текста извлекаются поля. Разница одна — заметку можно
 * привязать к конкретному кадру: досняли камень на просвет и сказали, что
 * именно на нём видно.
 *
 * Всё, что можно, делается на устройстве: запись сохраняется до всякой сети,
 * и потеря связи посреди расшифровки не теряет ни звука, ни текста.
 */
import { db, enqueue } from '../db';
import { apiFetch, syncNow } from '../sync';
import { ulid } from '@core';

/** Почему текста нет. Слово «нет связи» на всё подряд — вранье, которое путает. */
export type NoteProblem = '' | 'offline' | 'auth' | 'server' | 'empty' | 'credits';

export interface SavedNote {
  voiceId: string;
  transcript: string;
  problem: NoteProblem;
}

interface Extracted { attribute: string; value: string | number; unit?: string; span?: [number, number] }

export async function saveVoiceNote(opts: {
  stoneId: string;
  blob: Blob;
  mime: string;
  durationMs?: number;
  /** Кадр, к которому относится сказанное. Без него заметка — про камень целиком. */
  photoMediaId?: string;
}): Promise<SavedNote> {
  const { stoneId, blob, mime, durationMs, photoMediaId } = opts;
  const mediaId = ulid();
  const voiceId = ulid();
  const now = new Date().toISOString();

  await db.media.put({ id: mediaId, subjectId: stoneId, role: 'audio', blob,
    mime, bytes: blob.size, uploaded: false, createdAt: now });
  await db.voices.put({ id: voiceId, subjectId: stoneId, subjectKind: 'stone',
    mediaId, photoMediaId, status: 'pending', durationMs, createdAt: now });
  await enqueue('voice.add', {
    id: voiceId, subjectId: stoneId, subjectKind: 'stone', mediaId, photoMediaId, durationMs,
  }, ulid());

  if (!navigator.onLine) {
    void syncNow();
    return { voiceId, transcript: '', problem: 'offline' };
  }

  try {
    const fd = new FormData();
    fd.append('file', blob, `note.${mime.includes('mp4') ? 'mp4' : 'webm'}`);
    const tr = await apiFetch('/api/transcribe', { method: 'POST', body: fd });
    if (!tr.ok) {
      const problem: NoteProblem = tr.status === 401 ? 'auth' : tr.status === 402 ? 'credits' : 'server';
      return { voiceId, transcript: '', problem };
    }

    const { text } = (await tr.json()) as { text: string };
    if (!text.trim()) {
      await db.voices.update(voiceId, { status: 'done', transcript: '' });
      return { voiceId, transcript: '', problem: 'empty' };
    }

    await db.voices.update(voiceId, { transcript: text, status: 'done' });
    await enqueue('voice.add', {
      id: voiceId, subjectId: stoneId, subjectKind: 'stone', mediaId, photoMediaId, transcript: text,
    }, ulid());

    // Поля из сказанного — той же дорогой, что на съёмке: предложение модели,
    // которое человек подтверждает в карточке, а не готовый факт.
    try {
      const ex = await apiFetch('/api/extract', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ transcript: text }),
      });
      if (ex.ok) {
        const { facts } = (await ex.json()) as { facts: Extracted[] };
        for (const f of facts) {
          const num = Number(String(f.value).replace(/[^\d.-]/g, ''));
          const fact = {
            id: ulid(), subjectType: 'stone', subjectId: stoneId, attribute: f.attribute,
            valueText: String(f.value), valueNum: Number.isFinite(num) ? num : null,
            unit: f.unit ?? null, sourceType: 'ai_extraction', confidence: 'low',
            sourceRef: { voiceNoteId: voiceId, span: f.span ?? null },
            needsReview: true, recordedAt: new Date().toISOString(),
          };
          await db.facts.put(fact as never);
          await enqueue('fact.add', fact, ulid());
        }
      }
    } catch { /* поля подождут: сервер разберёт ту же запись при синхронизации */ }

    void syncNow();
    return { voiceId, transcript: text, problem: '' };
  } catch {
    return { voiceId, transcript: '', problem: 'server' };
  }
}

/** Правка текста руками. Аудио не трогаем — сверить сказанное можно всегда. */
export async function saveNoteText(voiceId: string, stoneId: string, text: string) {
  const clean = text.trim();
  await db.voices.update(voiceId, { transcript: clean, status: 'done' });
  await enqueue('voice.add', {
    id: voiceId, subjectId: stoneId, subjectKind: 'stone', transcript: clean,
  }, ulid());
  void syncNow();
}

export const NOTE_PROBLEM_TEXT: Record<Exclude<NoteProblem, ''>, string> = {
  offline: 'Записано. Текста пока нет — нет связи; расшифруется, когда появится сеть.',
  auth: 'Записано, но расшифровать не вышло: приложение не узнало вас. Войдите заново.',
  server: 'Записано. Расшифровка не удалась — сервер не ответил; попробуем ещё раз при синхронизации.',
  empty: 'Записано, но речи не слышно. Проверьте микрофон и надиктуйте заново.',
  credits: 'Записано. Расшифровка не работает: на счёте модели закончились средства. Запись цела — как только счёт пополнят, текст появится сам.',
};
